import {
    VideoCommentDTO,
    VideoCommentPermissionsDTO,
    VideoCommentRepliesPageDTO,
    VideoCommentsPageDTO,
    VideoCommentSort,
    VideoCommentVoteDTO,
} from "../../../Models/DTOs/VideoCommentsDTO";

export interface VideoCommentThreadState {
    items: VideoCommentDTO[];
    nextCursor: string | null;
    total: number;
    loaded: boolean;
    loading: boolean;
    loadingMore: boolean;
    error: string | null;
}

export interface VideoCommentsState {
    sort: VideoCommentSort;
    pinnedComment: VideoCommentDTO | null;
    items: VideoCommentDTO[];
    nextCursor: string | null;
    total: number;
    permissions: VideoCommentPermissionsDTO;
    permissionsLoaded: boolean;
    loading: boolean;
    loadingMore: boolean;
    error: string | null;
    threads: Record<number, VideoCommentThreadState>;
    reportedCommentIds: Record<number, true>;
}

export const emptyVideoCommentPermissions: VideoCommentPermissionsDTO = {
    can_comment: false,
    can_vote: false,
    can_report: false,
    can_pin: false,
};

export const initialVideoCommentsState: VideoCommentsState = {
    sort: "top",
    pinnedComment: null,
    items: [],
    nextCursor: null,
    total: 0,
    permissions: emptyVideoCommentPermissions,
    permissionsLoaded: false,
    loading: false,
    loadingMore: false,
    error: null,
    threads: {},
    reportedCommentIds: {},
};

const emptyThreadState = (): VideoCommentThreadState => ({
    items: [],
    nextCursor: null,
    total: 0,
    loaded: false,
    loading: false,
    loadingMore: false,
    error: null,
});

export const mergeCommentsById = (
    existing: VideoCommentDTO[],
    incoming: VideoCommentDTO[]
): VideoCommentDTO[] => {
    const merged = [...existing];
    const indexes = new Map(merged.map((comment, index) => [comment.id, index]));
    incoming.forEach((comment) => {
        const index = indexes.get(comment.id);
        if (index === undefined) {
            indexes.set(comment.id, merged.length);
            merged.push(comment);
        } else {
            merged[index] = comment;
        }
    });
    return merged;
};

const sortRepliesOldestFirst = (
    comments: VideoCommentDTO[]
): VideoCommentDTO[] =>
    [...comments].sort((left, right) => {
        const byDate = Date.parse(left.created_at) - Date.parse(right.created_at);
        return byDate || left.id - right.id;
    });

const replaceComment = (
    comments: VideoCommentDTO[],
    updated: VideoCommentDTO
): VideoCommentDTO[] =>
    comments.map((comment) => (comment.id === updated.id ? updated : comment));

const applyVote = (
    comments: VideoCommentDTO[],
    vote: VideoCommentVoteDTO
): VideoCommentDTO[] =>
    comments.map((comment) =>
        comment.id === vote.comment_id
            ? {
                  ...comment,
                  viewer_vote: vote.viewer_vote,
                  upvotes: vote.upvotes,
                  downvotes: vote.downvotes,
                  score: vote.score,
                  version: vote.version,
              }
            : comment
    );

export type VideoCommentsAction =
    | { type: "SET_SORT"; sort: VideoCommentSort }
    | { type: "ROOTS_LOADING"; append: boolean }
    | { type: "ROOTS_SUCCESS"; page: VideoCommentsPageDTO; append: boolean }
    | { type: "ROOTS_ERROR"; message: string; append: boolean }
    | { type: "THREAD_LOADING"; rootId: number; append: boolean }
    | {
          type: "THREAD_SUCCESS";
          rootId: number;
          page: VideoCommentRepliesPageDTO;
          append: boolean;
      }
    | { type: "THREAD_ERROR"; rootId: number; message: string; append: boolean }
    | { type: "COMMENT_UPDATED"; comment: VideoCommentDTO }
    | { type: "REPLY_CREATED"; comment: VideoCommentDTO }
    | { type: "VOTE_UPDATED"; vote: VideoCommentVoteDTO }
    | { type: "COMMENT_REPORTED"; commentId: number }
    | { type: "RESET" };

export const videoCommentsReducer = (
    state: VideoCommentsState,
    action: VideoCommentsAction
): VideoCommentsState => {
    switch (action.type) {
        case "SET_SORT":
            return {
                ...initialVideoCommentsState,
                sort: action.sort,
                reportedCommentIds: state.reportedCommentIds,
            };
        case "ROOTS_LOADING":
            return {
                ...state,
                loading: !action.append,
                loadingMore: action.append,
                error: action.append ? state.error : null,
            };
        case "ROOTS_SUCCESS":
            return {
                ...state,
                pinnedComment: action.page.pinned_comment,
                items: action.append
                    ? mergeCommentsById(state.items, action.page.items)
                    : action.page.items,
                nextCursor: action.page.next_cursor,
                total: action.page.total,
                permissions: action.page.permissions,
                permissionsLoaded: true,
                loading: false,
                loadingMore: false,
                error: null,
            };
        case "ROOTS_ERROR":
            return {
                ...state,
                loading: false,
                loadingMore: false,
                error: action.message,
            };
        case "THREAD_LOADING": {
            const current = state.threads[action.rootId] ?? emptyThreadState();
            return {
                ...state,
                threads: {
                    ...state.threads,
                    [action.rootId]: {
                        ...current,
                        loading: !action.append,
                        loadingMore: action.append,
                        error: action.append ? current.error : null,
                    },
                },
            };
        }
        case "THREAD_SUCCESS": {
            const current = state.threads[action.rootId] ?? emptyThreadState();
            const items = action.append
                ? sortRepliesOldestFirst(
                      mergeCommentsById(current.items, action.page.items)
                  )
                : sortRepliesOldestFirst(action.page.items);
            return {
                ...state,
                permissions: {
                    ...action.page.permissions,
                    // Pin management is a video-level permission supplied by
                    // the root listing. Loading a thread must not replace it.
                    can_pin: state.permissions.can_pin,
                },
                threads: {
                    ...state.threads,
                    [action.rootId]: {
                        items,
                        nextCursor: action.page.next_cursor,
                        total: action.append
                            ? Math.max(current.total, action.page.total, items.length)
                            : action.page.total,
                        loaded: true,
                        loading: false,
                        loadingMore: false,
                        error: null,
                    },
                },
            };
        }
        case "THREAD_ERROR": {
            const current = state.threads[action.rootId] ?? emptyThreadState();
            return {
                ...state,
                threads: {
                    ...state.threads,
                    [action.rootId]: {
                        ...current,
                        loaded: true,
                        loading: false,
                        loadingMore: false,
                        error: action.message,
                    },
                },
            };
        }
        case "COMMENT_UPDATED": {
            const threads = Object.fromEntries(
                Object.entries(state.threads).map(([rootId, thread]) => [
                    rootId,
                    { ...thread, items: replaceComment(thread.items, action.comment) },
                ])
            );
            return {
                ...state,
                pinnedComment:
                    state.pinnedComment?.id === action.comment.id
                        ? {
                              ...action.comment,
                              // Edit responses do not carry pin attribution.
                              // Editing a comment cannot change who pinned it.
                              pinned_by:
                                  action.comment.pinned_by ??
                                  state.pinnedComment.pinned_by,
                          }
                        : state.pinnedComment,
                items: replaceComment(state.items, action.comment),
                threads,
            };
        }
        case "REPLY_CREATED": {
            const rootId = action.comment.root_comment_id;
            if (rootId === null) return state;
            const existingThread = state.threads[rootId];
            const alreadyPresent = existingThread?.items.some(
                (comment) => comment.id === action.comment.id
            );
            const thread = existingThread
                ? {
                      ...existingThread,
                      items: sortRepliesOldestFirst(
                          mergeCommentsById(existingThread.items, [action.comment])
                      ),
                      total: existingThread.total + (alreadyPresent ? 0 : 1),
                  }
                : undefined;
            const incrementReplyCount = (comment: VideoCommentDTO) =>
                comment.id === rootId && !alreadyPresent
                    ? { ...comment, reply_count: comment.reply_count + 1 }
                    : comment;
            return {
                ...state,
                pinnedComment: state.pinnedComment
                    ? incrementReplyCount(state.pinnedComment)
                    : null,
                items: state.items.map(incrementReplyCount),
                threads: thread
                    ? { ...state.threads, [rootId]: thread }
                    : state.threads,
            };
        }
        case "VOTE_UPDATED": {
            const threads = Object.fromEntries(
                Object.entries(state.threads).map(([rootId, thread]) => [
                    rootId,
                    { ...thread, items: applyVote(thread.items, action.vote) },
                ])
            );
            return {
                ...state,
                pinnedComment:
                    state.pinnedComment?.id === action.vote.comment_id
                        ? applyVote([state.pinnedComment], action.vote)[0]
                        : state.pinnedComment,
                items: applyVote(state.items, action.vote),
                threads,
            };
        }
        case "COMMENT_REPORTED":
            return {
                ...state,
                reportedCommentIds: {
                    ...state.reportedCommentIds,
                    [action.commentId]: true,
                },
            };
        case "RESET":
            return { ...initialVideoCommentsState, sort: state.sort };
        default:
            return state;
    }
};
